/**
 * Date formatting for the admin surface.
 *
 * There were ten implementations under five names — `when()`, `fmtDate()`,
 * `fmtTs()`, `fmtWhen()`, `fmt()` — plus two places formatting inline in the
 * template. They disagreed about the small things that matter: whether an
 * unparseable value shows as `—` or as the raw string, and whether an empty
 * one renders as "Invalid Date".
 *
 * One rule here: a value we cannot read renders as an em dash, never as
 * "Invalid Date" and never as an empty cell. An empty cell reads as "there
 * is no value"; a broken one should read as "something is wrong".
 */

/** Em dash — what every formatter returns for absent or unreadable input. */
export const EMPTY = "—";

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date + time in the reader's locale. The default for "when did this happen". */
export function fmtDateTime(iso: string | null | undefined): string {
  return parse(iso)?.toLocaleString() ?? EMPTY;
}

/** Date only, in the reader's locale. For review dates and other day-grained values. */
export function fmtDate(iso: string | null | undefined): string {
  return parse(iso)?.toLocaleDateString() ?? EMPTY;
}

/**
 * `YYYY-MM-DD`, deliberately NOT localised.
 *
 * Governance dates are the ones that end up quoted in a compliance document
 * and read by someone in another country; `03/04/2026` is ambiguous to half
 * of them. Keep ISO where the value will be copied out of the product.
 */
export function fmtIsoDate(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? d.toISOString().slice(0, 10) : EMPTY;
}

/**
 * How long ago, in words: "just now", "4h ago", "3 days ago".
 *
 * For freshness columns, where the question is "is this current?" — an
 * absolute timestamp makes the reader do the subtraction themselves, which
 * is exactly the work a table should be doing for them. Pair it with the
 * absolute value in a `title` when the precise moment still matters.
 */
export function fmtAge(
  iso: string | null | undefined,
  now: Date = new Date(),
): string {
  const d = parse(iso);
  if (!d) return EMPTY;
  const mins = Math.round((now.getTime() - d.getTime()) / 60000);
  if (mins < 0) return fmtDateTime(iso); // clock skew, or genuinely scheduled ahead
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 31) return days === 1 ? "yesterday" : `${days} days ago`;
  return fmtDate(iso);
}
